import { useState, useRef, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { saveSession, saveFullSession, loadSession, getGroqKey, saveGroqKey } from "../History";


// ── Shared tail appended to every system prompt ───────────────────────────────
const SHARED_TAIL = `

---
UNIVERSAL OUTPUT REQUIREMENTS (apply to every format):

At the very top of your output, before the main title, include a metadata block:

---
**Title:** (auto-generate a precise, descriptive title from the content)
**Estimated reading time:** (calculate as total word count ÷ 200, round to nearest minute, e.g. "4 min read")
**Tone:** (single word only — choose the most accurate: formal / conversational / impassioned / academic / devotional / instructional / lyrical)
**Transcription confidence:** (assess how coherent and complete the raw transcript is — High / Medium / Low — and note why if Medium or Low)
---

After the main body, pull 2–3 of the most powerful or memorable lines from the content and present them as standout quotes using markdown blockquote format:

> "Quote here."

Choose quotes that capture the essence, a turning point, or something genuinely striking. Do not choose generic filler lines.

Be thorough. Do not truncate or summarise sections prematurely. A comprehensive output is always preferred over a brief one.`;

// ── Format style configs ──────────────────────────────────────────────────────
const FORMAT_TYPES = [
  {
    id: "meeting",
    label: "Meeting",
    sub: "Minutes & action items",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
        <rect x="1" y="3" width="14" height="10" rx="2" />
        <path d="M5 7h6M5 10h4" />
      </svg>
    ),
    systemPrompt: `You are a highly skilled professional secretary with decades of experience documenting board meetings, executive sessions, town halls, and corporate conferences at the highest level. Your minutes are trusted by executives, legal teams, and boards. Transform the raw transcript into comprehensive, polished, professional meeting minutes.

STRUCTURE — follow this exactly, expanding each section as fully as the transcript allows:

# Meeting Minutes

---
(metadata block goes here — see universal requirements below)
---

## Meeting Overview
- **Date & Time:** (infer from context or mark as [Not stated])
- **Location / Platform:** (in-person, Zoom, Teams, etc. — infer if possible)
- **Duration:** (estimate from context or segment timestamps if available)
- **Meeting Type:** (board meeting / team sync / project review / client call / etc.)
- **Facilitator / Chair:** (name if mentioned, else [Not identified])

## Attendees & Roles
(List every person mentioned by name or role. Format as:)
- **[Name]** — [Role/Title if known] — [present / joined late / left early if mentioned]
If names are unclear: [Unidentified speaker 1], [Unidentified speaker 2], etc.

## Agenda Items Discussed
(Number each distinct topic raised. For each item:)
### 1. [Topic Title]
**Raised by:** [name or role]
**Discussion summary:** (detailed summary of what was said, by whom where identifiable)
**Outcome:** (resolved / tabled / referred / ongoing)

### 2. [Topic Title]
(continue for all topics)

## Key Decisions Made
(Every confirmed decision, clearly stated. Format as:)
- **Decision:** [what was decided] | **Made by / Agreed by:** [name or group] | **Effective:** [date or immediately]
If no decisions were made, write: *No formal decisions recorded in this session.*

## Voting Record
(Only include if votes occurred. Format as:)
| Motion | For | Against | Abstain | Result |
|--------|-----|---------|---------|--------|
If no votes: omit this section entirely.

## Action Items
| # | Action | Assigned To | Due Date | Priority |
|---|--------|-------------|----------|----------|
(Extract every commitment, task, or follow-up mentioned. Mark unknown owners as TBD. Mark unknown deadlines as TBD. Assign priority: High / Medium / Low based on urgency conveyed.)

## Parking Lot
(Topics raised but deliberately deferred, tabled, or not fully addressed — list each with a brief note on why it was parked and who raised it.)
- [Topic] — raised by [name/role] — [reason deferred or context]
If nothing was parked: *No items deferred.*

## Clarifications Needed
(Flag anything in the transcript that was ambiguous, unclear, or contradictory.)
- [Clarification needed]: [describe the ambiguity]
Mark unclear audio as [unclear audio].

## Next Steps & Follow-ups
(Summarise what needs to happen before the next meeting — beyond the action items table. Include any mentioned next meeting date/time.)

Write in formal third-person throughout. Be exhaustive — err on the side of more detail, not less. A well-documented meeting record protects everyone.` + SHARED_TAIL,
  },
  {
    id: "academic",
    label: "Academic",
    sub: "Deep study notes",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
        <path d="M8 2L1 6l7 4 7-4-7-4z" />
        <path d="M1 6v5M4 8v4c0 1.1 1.8 2 4 2s4-.9 4-2V8" />
      </svg>
    ),
    systemPrompt: `You are an exceptionally gifted student — a genuine prodigy who attends lectures, seminars, and academic talks. You don't merely transcribe; you actively listen, think critically, connect concepts across disciplines, and read between the lines of what the speaker is saying. Your notes are legendary among your peers — comprehensive, insightful, and alive with your own thinking woven through the professor's words.

STRUCTURE — follow this exactly, expanding every section as fully as the content allows:

# Lecture Notes — [infer precise topic from content]
**Subject / Course:** [infer if possible]
**Speaker / Lecturer:** [name and title if mentioned]
**Institution / Context:** [if mentioned]

---
(metadata block goes here — see universal requirements below)
---

## Core Thesis / Main Argument
(What is the speaker fundamentally arguing or teaching? State it clearly and precisely in 2–4 sentences. This is not a summary — it is the central claim or purpose of the lecture.)

## Key Concepts & Definitions
(List every significant term, concept, framework, or theory introduced. For each:)
### [Concept Name]
**Definition as stated:** (what the speaker said, paraphrased precisely)
**In my own words:** (your restatement — simpler, more memorable)
**Example given:** (any example the speaker used)
**[*] My insight:** (your own deeper understanding, connection, or question about this concept)

## Lecture Outline — Argument Flow
(Map the logical progression of the lecture. Show how the speaker moved from idea to idea.)
1. [Opening point or hook]
2. [Development]
3. [Key turn or pivot]
4. [Supporting evidence or examples]
5. [Conclusion or call to reflection]

## Critical Insights
(The most important, surprising, counterintuitive, or nuanced points in the lecture. These are the things that separate a good student from a great one.)
- **[Insight title]:** [Explain why this matters and what it reveals]
Mark your own inferences clearly: [*] = reading between the lines

## Connections & Implications
(How does this content connect to other subjects, real-world events, prior lectures, or broader intellectual traditions? Be specific.)
- [Connection to X]: [explain the link]
- [Implication for Y]: [explain what follows]

## Glossary of Terms
(Every defined or specialised term used in the lecture, alphabetically ordered.)
| Term | Definition | Source (speaker / inferred) |
|------|------------|----------------------------|

## ⚠ Likely Testable / Exam Flags
(Flag anything the speaker emphasised, repeated, slowed down for, or explicitly marked as important.)
- [Topic or concept] — [why it seems testable]

## Recommended Readings & References
(Any books, papers, authors, datasets, or resources mentioned — direct or implied.)
- [Reference] — [context in which it was mentioned]
If none mentioned: *No readings cited in this session.*

## Questions to Explore
(What is left unanswered? What would you ask the speaker? What should you research further?)
1. [Question]
2. [Question]
3. [Question]

## Concept Hierarchy
(Show how the ideas in this lecture relate to each other in a simple outline tree.)
- [Overarching concept]
  - [Sub-concept]
    - [Detail or example]
  - [Sub-concept]

## Summary in 3 Sentences
(The entire lecture, distilled to its essence. Each sentence should carry real weight.)

Write with intellectual energy and genuine curiosity. Your annotations ([*]) are as valuable as the lecture itself.` + SHARED_TAIL,
  },
  {
    id: "music",
    label: "Music",
    sub: "Lyrics & song structure",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
        <path d="M9 3v8" />
        <path d="M9 3l5-1v3l-5 1" />
        <circle cx="6" cy="11" r="2.5" />
        <circle cx="12" cy="10" r="2.5" />
      </svg>
    ),
    systemPrompt: `You are a professional music transcriptionist, lyricist, and music analyst with deep knowledge of song structure, rhyme schemes, and genre conventions. Your job is to take a raw audio transcript and format it as a comprehensive, properly structured song document — the kind a music publisher, producer, or recording artist would actually use.

STRUCTURE — follow this exactly:

# [Song Title — infer from recurring themes, hook lines, or emotional core. If truly unclear, write "Untitled"]
**Artist / Speaker:** [if mentioned, else leave blank]
**Genre / Style:** [infer precisely from language, rhythm, and content — be specific: e.g. "Afrobeats gospel fusion" not just "gospel"]
**Key Theme:** [one sentence on what the song is about at its deepest level]

---
(metadata block goes here — see universal requirements below)
---

## Song Structure Map
(Show the full architecture of the song at a glance.)
> Intro → Verse 1 → Pre-Chorus → Chorus → Verse 2 → Chorus → Bridge → Chorus → Outro
(Adjust to match the actual structure you detect)

## Tempo & Feel
- **Estimated tempo:** [Slow ballad / Mid-tempo / Uptempo / Driving / Laid-back / etc.]
- **Emotional arc:** [Describe how the song moves emotionally from start to finish — e.g. "Opens with vulnerability, builds through determination, resolves in triumph"]
- **Instrumentation cues:** [Any instruments, sounds, or production elements referenced or implied in the lyrics]

---

## Lyrics

[Intro]
(any intro lines — if purely instrumental, note: *Instrumental intro*)

[Verse 1]
(Format each line on its own line. Preserve the natural phrasing and breath points of the singer.)
(Annotate rhyme scheme at the end of each line in brackets: A, B, C etc.)
Line one of the verse here (A)
Line two of the verse here (B)
Line three continues (A)
Line four resolves (B)

[Pre-Chorus] *(if present)*
(transition lines building to the chorus)

[Chorus]
(The repeating section — identify it by repetition or emotional peak)
(Mark clearly — this is the song's emotional and melodic centre)

[Post-Chorus] *(if present)*

[Verse 2]
(continue same annotation style)

[Bridge]
(the departure — usually a shift in perspective, key, or emotion)

[Outro / Coda]
(closing lines — note if it fades, resolves, or ends abruptly)

---

## Rhyme Scheme Analysis
(Summarise the dominant rhyme patterns used across the song.)
- Verse pattern: [e.g. ABAB / AABB / ABCB]
- Chorus pattern: [e.g. AABB]
- Bridge pattern: [e.g. free verse / AAAA]
- Internal rhymes / near rhymes noted: [list any notable ones]

## Lyrical Themes & Imagery
(What symbols, metaphors, or recurring images does the songwriter use? What is the song really about beneath the surface?)

## Production Notes
(Any observations about how this song should sound, based on the lyrics and feel. Tempo suggestions, mood, potential genre tags for streaming platforms.)

Mark any unclear or inaudible words as [?]. Do not guess at words you are not confident about. Also ensure that you dont't leave any word out` + SHARED_TAIL,
  },
  {
    id: "sermon",
    label: "Sermon",
    sub: "Theological sermon notes",
    icon: (
      <svg width="18" height="18" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4">
        <path d="M8 1v14M4 4h8M3 8h10M4 12h8" />
      </svg>
    ),
    systemPrompt: `You are an expert theological scribe, seminary-trained documentarian, and student of homiletics — the art and science of preaching. You understand how sermons are constructed: the hook, the text, the exposition, the illustration, the application, and the altar call. Your notes honour the message, preserve its spiritual power, and make it easy to study, share, teach from, and meditate on for weeks after it was delivered.

STRUCTURE — follow this exactly, expanding every section as fully as the content allows:

# [Sermon Title — infer from the central theme, a key phrase, or the dominant scripture. Make it compelling.]
**Speaker / Preacher:** [full name and title if mentioned, else "Pastor / Speaker"]
**Church / Ministry:** [if mentioned]
**Series:** [if this is part of a named series, note it]
**Date:** [if mentioned]
**Primary Scripture:** [the main text the message is built on]
**Additional Scriptures:** [all other verses cited or alluded to]
**Doctrinal Category:** [tag with 1–3 categories: e.g. Salvation / Faith / Grace / Prophecy / Healing / Identity / Prayer / Stewardship / Holiness / etc.]
**Central Theme:** [one powerful sentence that captures the heart of the entire message]

---
(metadata block goes here — see universal requirements below)
---

## Opening & Introduction
(How did the preacher open? Was there a story, a joke, a provocative question, a cultural reference, a personal testimony? Capture it fully — the opening sets the entire tone.)

## Scripture Foundation
(Present the primary scripture passage in full, as referenced. Note the translation if mentioned. Explain briefly why this text anchors the message.)

## Main Points
(Sermons typically have 3–5 main points. Identify each and document it fully.)

### Point 1: [Title — make it memorable, alliterative if the preacher used that style]
**Scripture(s) used:** [verse references]
**Exposition:** (What did the preacher say about this point? Be detailed — include the argument, the logic, the pastoral heart behind it.)
**Illustration / Story used:** (any story, analogy, or example given to support this point)
**[Key moment]:** (if the preacher repeated a phrase, raised their voice, or made a particularly powerful statement — flag it here)
**Application:** (how did they connect this point to the listener's daily life?)

### Point 2: [Title]
(same structure)

### Point 3: [Title]
(same structure — add Point 4 and 5 if present)

## Original Language References
(If the preacher referenced Greek, Hebrew, or Aramaic words — note them here with their meaning and significance.)
| Word | Language | Meaning | Context in sermon |
|------|----------|---------|-------------------|
If none referenced: *No original language references noted.*

## Key Scriptures & Cross-References
(Every scripture cited, alluded to, or quoted — direct and indirect. Include suggested cross-references that enrich the message even if not explicitly stated by the preacher.)
| Scripture | How used | Cross-reference suggestion |
|-----------|----------|--------------------------|

## Illustrations, Stories & Examples Used
(Document every story, analogy, personal testimony, historical reference, or cultural illustration used — these are often the most memorable parts of a sermon.)
1. [Story/Illustration title]: [full description]

## Moments of Emphasis
(Phrases the preacher repeated, sentences they slowed down for, moments the congregation responded — these are the sermon's power points.)
- **[Key moment]:** "[phrase or statement]"

## Altar Call & Personal Application
(The call to action — what did the preacher ask listeners to do, believe, or change? Be specific.)

## Closing Prayer / Benediction
(The closing words, blessing, or prayer — capture it as fully as possible.)

## Small Group Discussion Questions
(Generate 5 thoughtful discussion questions based on the message — suitable for a home group, Bible study, or personal reflection.)
1. [Question]
2. [Question]
3. [Question]
4. [Question]
5. [Question]

## Reflection Notes
(Your own synthesis — what is the single most important thing a listener should take away from this message? What would change in someone's life if they truly applied it?)

Preserve the spiritual weight and passion of the language. Bold the most powerful statements. This document should feel like sitting in that church again.` + SHARED_TAIL,
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────
function formatTime(d = new Date()) {
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function isValidKey(k) {
  return typeof k === "string" && k.startsWith("gsk_") && k.length > 20;
}

// ── Styles ────────────────────────────────────────────────────────────────────
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --bg: #faf9f8;
  --surface: #ffffff;
  --surface2: #f3f2f1;
  --border: rgba(0,0,0,0.08);
  --border2: rgba(0,0,0,0.14);
  --text: #1a1a1a;
  --text2: #5a5a5a;
  --text3: #9a9a9a;
  --accent: #c8571a;
  --accent-bg: #fff3ed;
  --accent-border: rgba(200,87,26,0.25);
  --info: #1a5fc8;
  --info-bg: #f0f4ff;
  --info-border: rgba(26,95,200,0.25);
  --success: #2a7a3b;
  --success-bg: #f0faf3;
  --radius: 6px;
  --radius-sm: 4px;
  --shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
  --shadow-md: 0 4px 12px rgba(0,0,0,0.08);
}

body {
  font-family: 'Inter', -apple-system, sans-serif;
  background: var(--bg);
  color: var(--text);
  min-height: 100vh;
  line-height: 1.6;
}

.app-wrap {
  max-width: 800px;
  margin: 0 auto;
  padding: 28px 24px 80px;
}

/* Header */
.app-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
  padding-bottom: 18px;
  border-bottom: 0.5px solid var(--border);
}
.app-title { font-size: 20px; font-weight: 600; letter-spacing: -0.3px; color: var(--text); }
.app-sub { font-size: 13px; color: var(--text3); margin-top: 3px; }
.status-pill {
  font-size: 11px;
  font-weight: 500;
  padding: 4px 12px;
  border-radius: 20px;
  border: 0.5px solid var(--border);
  background: var(--surface2);
  color: var(--text3);
  white-space: nowrap;
  transition: all 0.2s;
  font-family: 'JetBrains Mono', monospace;
  letter-spacing: 0.04em;
}
.status-pill.recording {
  background: #fff0ee;
  color: #c8571a;
  border-color: rgba(200,87,26,0.3);
  animation: blink 1.4s ease-in-out infinite;
}
.status-pill.processing {
  background: var(--info-bg);
  color: var(--info);
  border-color: var(--info-border);
}
@keyframes blink { 0%,100%{opacity:1} 50%{opacity:0.55} }

/* Cards */
.card {
  background: var(--surface);
  border: 0.5px solid var(--border);
  border-radius: var(--radius);
  padding: 20px;
  box-shadow: var(--shadow);
  margin-bottom: 12px;
  transition: border-color 0.15s;
}
.card:hover { border-color: var(--border2); }

/* Key section */
.field-label {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  color: var(--text3);
  margin-bottom: 7px;
  font-family: 'JetBrains Mono', monospace;
}
.key-input-wrap { position: relative; }
.key-input {
  width: 100%;
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  padding: 8px 13px;
  border: 0.5px solid var(--border);
  border-radius: var(--radius-sm);
  background: var(--surface2);
  color: var(--text);
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.key-input:focus { border-color: var(--border2); box-shadow: 0 0 0 3px rgba(0,0,0,0.04); background: var(--surface); }
.key-input.valid { border-color: rgba(42,122,59,0.4); background: var(--success-bg); }
.key-hint {
  font-size: 12px;
  color: var(--text3);
  margin-top: 6px;
}
.key-hint.valid { color: var(--success); }

/* Action buttons row */
.actions-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 12px;
}
.btn {
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  height: 36px;
  padding: 0 16px;
  border-radius: var(--radius-sm);
  border: 0.5px solid var(--border);
  background: var(--surface);
  color: var(--text);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: background 0.15s, border-color 0.15s, box-shadow 0.15s;
  white-space: nowrap;
}
.btn:hover:not(:disabled) {
  background: #f9f9f9;
  border-color: var(--border2);
  box-shadow: 0 1px 4px rgba(0,0,0,0.06);
}
.btn:active:not(:disabled) { transform: scale(0.99); }
.btn:disabled { opacity: 0.35; cursor: not-allowed; }
.btn-record { background: var(--accent-bg); color: var(--accent); border-color: var(--accent-border); }
.btn-record:hover:not(:disabled) { background: #ffe8da; border-color: rgba(200,87,26,0.4); }
.btn-record.active { background: var(--accent); color: #fff; border-color: var(--accent); }
.btn-record.active:hover:not(:disabled) { background: #b04a14; }

/* Format section */
.format-divider {
  border: none;
  border-top: 0.5px solid var(--border);
  margin: 16px 0;
}
.format-chips {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 8px;
  margin-bottom: 10px;
}
.fmt-chip {
  border: 0.5px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 10px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--text2);
  background: var(--surface);
  transition: all 0.15s;
  user-select: none;
}
.fmt-chip:hover { border-color: var(--border2); color: var(--text); background: #f9f9f9; box-shadow: 0 1px 4px rgba(0,0,0,0.05); }
.fmt-chip.selected {
  border-color: rgba(26,95,200,0.35);
  background: var(--info-bg);
  color: var(--info);
}
.fmt-chip-sub { font-size: 10px; color: var(--text3); margin-top: 1px; text-align: center; font-family: 'JetBrains Mono', monospace; letter-spacing: 0.04em; }
.fmt-chip.selected .fmt-chip-sub { color: var(--info); opacity: 0.75; }
.btn-format {
  width: 100%;
  height: 36px;
  background: var(--info-bg);
  color: var(--info);
  border-color: var(--info-border);
  font-weight: 500;
}
.btn-format:hover:not(:disabled) { background: #e4ecfd; border-color: rgba(26,95,200,0.4); }

/* Progress */
.progress-wrap { margin-top: 12px; }
.progress-bar {
  height: 2px;
  background: var(--surface2);
  border-radius: 4px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  background: var(--info);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.waveform {
  display: flex;
  align-items: center;
  gap: 3px;
  height: 28px;
  padding: 0 4px;
}
.waveform-bar {
  width: 3px;
  border-radius: 2px;
  background: var(--accent, #c8571a);
  animation: wave 0.8s ease-in-out infinite alternate;
}
.waveform-bar:nth-child(1) { height: 8px; }
.waveform-bar:nth-child(2) { height: 16px; }
.waveform-bar:nth-child(3) { height: 24px; }
.waveform-bar:nth-child(4) { height: 16px; }
.waveform-bar:nth-child(5) { height: 8px; }
@keyframes wave {
  from { transform: scaleY(0.4); opacity: 0.6; }
  to   { transform: scaleY(1);   opacity: 1; }
}

.progress-meta {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: var(--text3);
  margin-top: 5px;
  font-family: 'JetBrains Mono', monospace;
}

/* Transcript card */
.transcript-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}
.transcript-meta {
  display: flex;
  gap: 12px;
}
.transcript-meta span {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  color: var(--text3);
}
.transcript-actions { display: flex; gap: 5px; }
.btn-sm {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  height: 28px;
  padding: 0 10px;
  border-radius: var(--radius-sm);
  border: 0.5px solid var(--border);
  background: var(--surface);
  color: var(--text2);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 5px;
  transition: all 0.15s;
}
.btn-sm:hover:not(:disabled) { background: var(--surface2); color: var(--text); border-color: var(--border2); }
.btn-sm:disabled { opacity: 0.28; cursor: not-allowed; }

.transcript-box {
  background: var(--surface2);
  border: 0.5px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 16px;
  min-height: 300px;
  max-height: 440px;
  overflow-y: auto;
  font-size: 14px;
  line-height: 1.8;
  color: var(--text);
  white-space: pre-wrap;
  font-family: 'Inter', sans-serif;
}
.transcript-box--formatted {
  white-space: normal;
  background: var(--surface);
  max-height: 600px;
}

/* Formatted markdown content */
.formatted-content { font-family: 'Inter', sans-serif; font-size: 14px; line-height: 1.85; color: var(--text); }
.formatted-content h1 { font-size: 20px; font-weight: 600; margin: 0 0 12px; padding-bottom: 8px; border-bottom: 2px solid var(--accent); color: var(--text); letter-spacing: -0.3px; }
.formatted-content h2 { font-size: 16px; font-weight: 600; margin: 20px 0 8px; padding-bottom: 5px; border-bottom: 0.5px solid var(--border); color: var(--text); }
.formatted-content h3 { font-size: 14px; font-weight: 600; margin: 14px 0 5px; color: var(--accent); }
.formatted-content h4 { font-size: 13px; font-weight: 600; margin: 10px 0 4px; color: var(--text2); }
.formatted-content p { margin: 0 0 9px; }
.formatted-content ul, .formatted-content ol { padding-left: 20px; margin: 0 0 9px; }
.formatted-content li { margin-bottom: 4px; }
.formatted-content strong { font-weight: 600; color: var(--text); }
.formatted-content em { font-style: italic; color: var(--text2); }
.formatted-content blockquote { border-left: 2px solid var(--accent); margin: 12px 0; padding: 8px 14px; background: var(--accent-bg); border-radius: 0 var(--radius-sm) var(--radius-sm) 0; color: var(--text2); font-style: italic; font-size: 14px; }
.formatted-content blockquote p { margin: 0; }
.formatted-content table { width: 100%; border-collapse: collapse; margin: 10px 0 14px; font-size: 13px; }
.formatted-content th { background: var(--surface2); padding: 6px 12px; border: 0.5px solid var(--border); text-align: left; font-weight: 600; font-size: 11px; color: var(--text3); text-transform: uppercase; letter-spacing: 0.06em; font-family: 'JetBrains Mono', monospace; }
.formatted-content td { padding: 6px 12px; border: 0.5px solid var(--border); vertical-align: top; }
.formatted-content tr:nth-child(even) td { background: var(--surface2); }
.formatted-content code { font-family: 'JetBrains Mono', monospace; font-size: 12px; background: var(--surface2); border: 0.5px solid var(--border); padding: 1px 5px; border-radius: 3px; }
.formatted-content pre { background: var(--surface2); border: 0.5px solid var(--border); border-radius: var(--radius-sm); padding: 12px; overflow-x: auto; margin: 10px 0; }
.formatted-content pre code { background: none; border: none; padding: 0; }
.formatted-content hr { border: none; border-top: 0.5px solid var(--border); margin: 16px 0; }

/* PDF export button */
.btn-sm-pdf { background: #fdf4e3; color: #8a5a00; border-color: rgba(138,90,0,0.25); }
.btn-sm-pdf:hover:not(:disabled) { background: #f5e8c0; color: #6b4500; }

/* Model selector */
.model-selector-wrap {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 0.5px solid var(--border);
}
.model-chips {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 6px;
}
.model-chip {
  border: 0.5px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 8px 6px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  cursor: pointer;
  font-size: 11px;
  font-weight: 500;
  color: var(--text2);
  background: var(--surface2);
  transition: all 0.15s;
  text-align: center;
  user-select: none;
}
.model-chip:hover { border-color: var(--border2); color: var(--text); }
.model-chip.selected {
  border-color: var(--accent-border);
  background: var(--accent-bg);
  color: var(--accent);
}
.model-chip-sub {
  font-size: 9px;
  color: var(--text3);
  font-weight: 400;
}
.model-chip.selected .model-chip-sub { color: var(--accent); opacity: 0.75; }
body.dark .model-chip { background: #2d2c2b; border-color: rgba(255,255,255,0.08); color: #b3b0ad; }
body.dark .model-chip:hover { border-color: rgba(255,255,255,0.14); color: #f3f2f1; }
body.dark .model-chip.selected { background: rgba(200,87,26,0.12); border-color: rgba(200,87,26,0.3); color: #e07840; }
.transcript-placeholder {
  color: var(--text3);
  font-style: italic;
  font-size: 13px;
}
.segment { margin-bottom: 10px; }
.segment-time {
  display: block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: var(--text3);
  margin-bottom: 3px;
  letter-spacing: 0.04em;
}
.seg-divider {
  border: none;
  border-top: 0.5px solid var(--border);
  margin: 10px 0;
}
.rec-dot {
  display: inline-block;
  width: 6px; height: 6px;
  border-radius: 50%;
  background: var(--accent);
  animation: blink 1s infinite;
  margin-right: 6px;
}

/* Toast */
.toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  background: var(--text);
  color: #fff;
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  padding: 8px 14px;
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  z-index: 999;
  pointer-events: none;
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.2s, transform 0.2s;
}
.toast.show { opacity: 1; transform: translateY(0); }

/* ── Dark mode ── */
body.dark {
  --bg: #1b1a19;
  --surface: #252423;
  --surface2: #2d2c2b;
  --border: rgba(255,255,255,0.08);
  --border2: rgba(255,255,255,0.14);
  --text: #f3f2f1;
  --text2: #b3b0ad;
  --text3: #797775;
  --accent-bg: rgba(200,87,26,0.12);
  --accent-border: rgba(200,87,26,0.25);
  --info-bg: rgba(26,95,200,0.1);
  --info-border: rgba(26,95,200,0.25);
  --success-bg: rgba(42,122,59,0.1);
}
body.dark .card { box-shadow: 0 2px 12px rgba(0,0,0,0.3); }
body.dark .card:hover { border-color: rgba(255,255,255,0.14); }
body.dark .key-input { background: #1b1a19; color: #f3f2f1; }
body.dark .key-input.valid { background: rgba(42,122,59,0.08); border-color: rgba(42,122,59,0.4); }
body.dark .transcript-box { background: #2d2c2b; border-color: rgba(255,255,255,0.08); color: #d4cfc8; }
body.dark .transcript-box--formatted { background: #252423; }
body.dark .formatted-content { color: #f3f2f1; }
body.dark .formatted-content h1 { color: #f3f2f1; border-color: var(--accent); }
body.dark .formatted-content h2 { color: #f3f2f1; border-color: rgba(255,255,255,0.08); }
body.dark .formatted-content h3 { color: #c8571a; }
body.dark .formatted-content h4 { color: #b3b0ad; }
body.dark .formatted-content blockquote { background: rgba(200,87,26,0.08); color: #b3b0ad; }
body.dark .formatted-content th { background: #2d2c2b; color: #b3b0ad; border-color: rgba(255,255,255,0.08); }
body.dark .formatted-content td { border-color: rgba(255,255,255,0.08); }
body.dark .formatted-content tr:nth-child(even) td { background: #2d2c2b; }
body.dark .formatted-content code { background: #2d2c2b; border-color: rgba(255,255,255,0.08); }
body.dark .formatted-content pre { background: #2d2c2b; border-color: rgba(255,255,255,0.08); }
body.dark .btn-sm { background: #2d2c2b; border-color: rgba(255,255,255,0.08); color: #b3b0ad; }
body.dark .btn-sm:hover:not(:disabled) { background: #323130; color: #f3f2f1; }
body.dark .btn-sm-pdf { background: rgba(138,90,0,0.12); color: #d4a84b; border-color: rgba(138,90,0,0.25); }
body.dark .fmt-chip { background: #2d2c2b; border-color: rgba(255,255,255,0.08); color: #b3b0ad; }
body.dark .fmt-chip:hover { border-color: rgba(255,255,255,0.14); color: #f3f2f1; }
body.dark .fmt-chip.selected { background: rgba(26,95,200,0.12); border-color: rgba(26,95,200,0.3); color: #6b9ef7; }
body.dark .btn-format { background: rgba(26,95,200,0.1); color: #6b9ef7; border-color: rgba(26,95,200,0.25); }
body.dark .btn-format:hover:not(:disabled) { background: rgba(26,95,200,0.18); }
body.dark .btn-record { background: rgba(200,87,26,0.1); color: #e07840; border-color: rgba(200,87,26,0.25); }
body.dark .btn-record.active { background: #c8571a; color: #fff; }
body.dark .progress-bar { background: #2d2c2b; }
body.dark ::-webkit-scrollbar-track { background: #1b1a19; }
body.dark ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.15); }
`;

// ── Main Component ─────────────────────────────────────────────────────────────
export default function Scribe() {
  const [apiKey, setApiKey] = useState(() => getGroqKey());
  const [segments, setSegments] = useState([]);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState({ label: "", pct: 0, visible: false });
  const [status, setStatus] = useState("idle");
  const [statusLabel, setStatusLabel] = useState("Idle");
  const [selectedFmt, setSelectedFmt] = useState(null);
  const [selectedModel, setSelectedModel] = useState(() => localStorage.getItem("scribe_model") || "llama-3.1-8b-instant");
  const [toast, setToast] = useState({ msg: "", show: false });
  const [isFormatted, setIsFormatted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formattedHtml, setFormattedHtml] = useState("");
  const recorderRef = useRef(null);
  const boxRef = useRef(null);
  const toastTimer = useRef(null);
  const [lastSync, setLastSync] = useState(null);
  const [searchParams] = useSearchParams();
  const rawSegmentsRef = useRef([]);
  const analyserRef = useRef(null);
  const animFrameRef = useRef(null);
  const [barHeights, setBarHeights] = useState([8, 8, 8, 8, 8]);

  const hasKey = isValidKey(apiKey);
  const hasContent = segments.length > 0;

  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [segments]);

  useEffect(() => {
    const sync = () => setApiKey(getGroqKey());
    window.addEventListener("storage", sync);
    return () => window.removeEventListener("storage", sync);
  }, []);

  useEffect(() => {
    const sessionId = searchParams.get("session");
    if (!sessionId) return;
    const saved = loadSession(sessionId);
    if (!saved) return;
    if (saved.segments?.length) setSegments(saved.segments);
    if (saved.rawSegments?.length) rawSegmentsRef.current = saved.rawSegments;
    if (saved.formattedHtml) {
      setFormattedHtml(saved.formattedHtml);
      setIsFormatted(true);
    }
    if (saved.selectedFmt) setSelectedFmt(saved.selectedFmt);
    showToast("Session restored");
  }, []);

  const showToast = useCallback((msg, dur = 2400) => {
    clearTimeout(toastTimer.current);
    setToast({ msg, show: true });
    toastTimer.current = setTimeout(() => setToast((t) => ({ ...t, show: false })), dur);
  }, []);

  const onKeyChange = (e) => {
    const v = e.target.value;
    setApiKey(v);
    saveGroqKey(v);
  };

  const setStatusInfo = (state, label) => {
    setStatus(state);
    setStatusLabel(label);
  };

  // ── Transcribe blob via Groq Whisper
  const transcribeBlob = async (blob) => {
    const fd = new FormData();
    fd.append("file", blob, "audio.webm");
    fd.append("model", "whisper-large-v3");
    const res = await fetch("https://api.groq.com/openai/v1/audio/transcriptions", {
      method: "POST",
      headers: { Authorization: `Bearer ${apiKey}` },
      body: fd,
    });
    if (!res.ok) {
      const e = await res.json().catch(() => ({}));
      throw new Error(e.error?.message || `API error ${res.status}`);
    }
    return (await res.json()).text || "";
  };

  // ── Record toggle

  const toggleRecord = async () => {
    if (!hasKey) return;
    if (!isRecording) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const audioCtx = new AudioContext();
        const source = audioCtx.createMediaStreamSource(stream);
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 32;
        source.connect(analyser);
        analyserRef.current = analyser;

        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteFrequencyData(data);
          setBarHeights([
            Math.max(4, data[1] / 4),
            Math.max(4, data[2] / 3),
            Math.max(4, data[3] / 2.5),
            Math.max(4, data[2] / 3),
            Math.max(4, data[1] / 4),
          ]);
          animFrameRef.current = requestAnimationFrame(tick);
        };
        tick();
        const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
          ? "audio/webm;codecs=opus"
          : "audio/webm";
        const recorder = new MediaRecorder(stream, { mimeType: mime });
        recorderRef.current = recorder;
        const allChunks = [];
        let lastChunkIndex = 0;

        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) allChunks.push(e.data);
        };

        const syncInterval = setInterval(async () => {
          if (allChunks.length === 0 || allChunks.length === lastChunkIndex) return;
          const blob = new Blob(allChunks, { type: mime });
          lastChunkIndex = allChunks.length;
          setStatusInfo("processing", "Syncing...");
          try {
            const text = await transcribeBlob(blob);
            if (text.trim()) {
              const seg = [{ time: formatTime(), text: text.trim() }];
              rawSegmentsRef.current = seg;
              setSegments(seg);
              setLastSync(new Date());
              setIsFormatted(false);
              setFormattedHtml("");
            }
          } finally {
            setStatusInfo("recording", "Recording");
          }
        }, 15000);

        recorder.onstop = async () => {
          clearInterval(syncInterval);
          if (allChunks.length === 0) return;
          const blob = new Blob(allChunks, { type: mime });
          setStatusInfo("processing", "Finalising...");
          try {
            const text = await transcribeBlob(blob);
            if (text.trim()) {
              const seg = [{ time: formatTime(), text: text.trim() }];
              rawSegmentsRef.current = seg;
              setSegments(seg);
              setLastSync(new Date());
              setIsFormatted(false);
              setFormattedHtml("");
            }
          } catch (err) {
            if (!navigator.onLine) {
              showToast("No internet — check your connection and try again.", 4000);
            } else if (err.message?.includes("Failed to fetch")) {
              showToast("Could not reach Groq — check your connection and retry.", 4000);
            } else {
              showToast("⚠️ Sync error: " + err.message, 4000);
            }
          } finally {
            setStatusInfo("idle", "Idle");
          }
        };

        recorder.start(1000);
        setIsRecording(true);
        setLastSync(null);
        setStatusInfo("recording", "Recording");
      } catch {
        showToast("Microphone access denied", 3000);
      }
    } else {
      recorderRef.current?.stop();
      recorderRef.current?.stream.getTracks().forEach((t) => t.stop());
      recorderRef.current = null;
      setIsRecording(false);
      cancelAnimationFrame(animFrameRef.current);
      setBarHeights([8, 8, 8, 8, 8]);
    }
  };

  // ── File upload
  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !hasKey) return;
    const CHUNK = 12 * 1024 * 1024;
    const total = Math.ceil(file.size / CHUNK);
    setIsProcessing(true);
    setStatusInfo("processing", "Processing");
    setProgress({ label: "Starting...", pct: 0, visible: true });
    try {
      for (let i = 0; i < total; i++) {
        const chunk = file.slice(i * CHUNK, Math.min((i + 1) * CHUNK, file.size));
        const pct = Math.round(((i + 1) / total) * 100);
        setProgress({
          label: total > 1 ? `Part ${i + 1} of ${total}` : "Transcribing...",
          pct,
          visible: true,
        });
        const text = await transcribeBlob(chunk);
        if (text.trim()) {
          setSegments((prev) => {
            const updated = [...prev, { time: formatTime(), text: text.trim() }];
            rawSegmentsRef.current = updated;
            return updated;
          });
          setIsFormatted(false);
          setFormattedHtml("");
        }
      }
      showToast("Upload complete");
    } catch (err) {
      if (!navigator.onLine) {
        showToast("No internet — check your connection and try again.", 4000);
      } else if (err.message?.includes("Failed to fetch")) {
        showToast("Could not reach Groq — check your connection and retry.", 4000);
      } else {
        showToast("Upload failed: " + err.message, 3500);
      }
    } finally {
      setProgress({ label: "", pct: 0, visible: false });
      setIsProcessing(false);
      setStatusInfo("idle", "Idle");
      e.target.value = "";
    }
  };

  // ── Auto-format
  const formatTranscript = async () => {
    if (!hasContent || !hasKey || !selectedFmt) return;
    const fmt = FORMAT_TYPES.find((f) => f.id === selectedFmt);
    const allText = (rawSegmentsRef.current.length > 0 ? rawSegmentsRef.current : segments).map((s) => s.text).join("\n\n");
    const wordCount2 = allText.split(/\s+/).filter(Boolean).length;

    // Under 4000 words — send full transcript with full system prompt (best quality)
    const USE_CHUNKS = fmt.id === "music" ? false : wordCount2 > 4000;
    const CHUNK_WORDS = 3500;


    // Short prompts only used when chunking large transcripts
    const SHORT_PROMPTS = {
      meeting: `You are a professional secretary. Format this transcript into comprehensive meeting minutes using markdown. Include: agenda items, key decisions, action items table (owner + due date), parking lot items. Be exhaustive — do not truncate.`,
      academic: `You are an expert student. Format this transcript into comprehensive academic study notes using markdown. Include: core thesis, key concepts with definitions, critical insights, argument flow, glossary, and exam flags. Be thorough.`,
      music: `You are a professional music transcriptionist. Format this transcript as a complete song document using markdown. You MUST include: song title, genre, song structure map (Intro→Verse→Chorus etc), full lyrics with [Verse 1] [Chorus] [Bridge] labels on each section, rhyme scheme annotations (A/B/C per line), lyrical themes analysis. Every line of lyrics on its own line.`,
      sermon: `You are a theological scribe. Format this transcript into detailed sermon notes using markdown. Include: title, scripture references, main points with exposition, illustrations used, key quotes, application points, and discussion questions. Preserve the spiritual weight.`,
    };
    const systemPrompt = USE_CHUNKS
      ? (SHORT_PROMPTS[fmt.id] || `Format this transcript into a well-structured ${fmt.label} document using markdown. Be detailed and thorough.`)
      : fmt.systemPrompt;

    const words = allText.split(/\s+/);
    const chunks = USE_CHUNKS
      ? Array.from({ length: Math.ceil(words.length / CHUNK_WORDS) }, (_, i) =>
        words.slice(i * CHUNK_WORDS, (i + 1) * CHUNK_WORDS).join(" "))
      : [allText];

    setIsProcessing(true);
    setStatusInfo("processing", "Formatting...");
    setProgress({ label: "Formatting...", pct: 0, visible: true });

    try {
      const formattedChunks = [];

      for (let i = 0; i < chunks.length; i++) {
        setProgress({
          label: chunks.length > 1 ? `Formatting part ${i + 1} of ${chunks.length}...` : "Formatting...",
          pct: Math.round((i / chunks.length) * 100),
          visible: true,
        });

        const isFirst = i === 0;
        const isLast = i === chunks.length - 1;
        const partNote = chunks.length > 1
          ? `\n\n[Part ${i + 1} of ${chunks.length}. ${isFirst ? "Start the document." : "Continue — do NOT restart headings or repeat the title."} ${isLast ? "Close and complete the document." : "More content follows — leave document open."}]`
          : "";

        const res = await fetch("https://api.groq.com/openai/v1/chat/completions", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${apiKey}`,
          },
          body: JSON.stringify({
            model: selectedModel,
            max_tokens: USE_CHUNKS ? 4096 : 8192,
            temperature: fmt.id === "music" ? 0.2 : 0.3,
            messages: [
              { role: "system", content: systemPrompt },
              {
                role: "user",
                content: "Format this transcript:\n\n" + chunks[i] + partNote,
              },
            ],
          }),
        });

        const d = await res.json();
        if (!res.ok) throw new Error(d?.error?.message || `API error ${res.status}`);

        const formatted = d.choices?.[0]?.message?.content;
        if (!formatted) throw new Error("Empty response from API");
        formattedChunks.push(formatted);

        if (i < chunks.length - 1) {
          await new Promise(r => setTimeout(r, 2500));
        }
      }

      setProgress({ label: "Stitching document...", pct: 95, visible: true });
      const combined = formattedChunks.join("\n\n---\n\n");

      if (!window.marked) {
        await new Promise((resolve, reject) => {
          const s = document.createElement("script");
          s.src = "https://cdn.jsdelivr.net/npm/marked/marked.min.js";
          s.onload = resolve;
          s.onerror = reject;
          document.head.appendChild(s);
        });
      }

      window.marked.setOptions({ breaks: true });
      const html = window.marked.parse(combined);
      setSegments([{ time: formatTime(), text: combined }]);
      setFormattedHtml(html);
      setIsFormatted(true);
      setIsEditing(false);

      const sessionId = Date.now().toString();
      saveSession({
        tool: "scribe",
        title: `${fmt.label} — ${new Date().toLocaleDateString()}`,
        subtitle: `${wordCount.toLocaleString()} words · ${fmt.label} format`,
        id: sessionId,
      });
      saveFullSession(sessionId, {
        segments: [{ time: new Date().toLocaleTimeString(), text: combined }],
        rawSegments: rawSegmentsRef.current,
        formattedHtml: html,
        selectedFmt: fmt.id,
      });

      showToast(chunks.length > 1 ? `Formatted in ${chunks.length} parts ✓` : `Formatted as ${fmt.label} ✓`);

    } catch (err) {
      showToast("Format failed: " + err.message, 5000);
    } finally {
      setIsProcessing(false);
      setStatusInfo("idle", "Idle");
      setProgress({ label: "", pct: 0, visible: false });
    }
  };
  const copyText = () => {
    const text = segments.map((s) => `[${s.time}]\n${s.text}`).join("\n\n");
    navigator.clipboard.writeText(text).then(() => showToast("Copied to clipboard"));
  };

  const downloadTxt = () => {
    const text = segments.map((s) => `[${s.time}]\n${s.text}`).join("\n\n---\n\n");
    const blob = new Blob([text], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `transcript-${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
  };

  const exportPdf = async () => {
    if (!isFormatted || !formattedHtml) return;
    showToast("Preparing PDF...", 8000);

    // Load jsPDF only once
    if (!window.jspdf) {
      await new Promise((resolve, reject) => {
        const s = document.createElement("script");
        s.src = "https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js";
        s.onload = resolve; s.onerror = reject;
        document.head.appendChild(s);
      });
    }



    const { jsPDF } = window.jspdf;
    const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

    const pageW = 210, pageH = 297;
    const marginL = 16, marginR = 16, marginT = 16, marginB = 16;
    const contentW = pageW - marginL - marginR;
    let y = marginT;

    const ACCENT = [200, 87, 26];
    const DARK = [26, 23, 20];
    const GREY = [107, 102, 95];
    const LIGHT = [226, 221, 216];

    const checkPage = (needed = 6) => {
      if (y + needed > pageH - marginB) { doc.addPage(); y = marginT; }
    };

    const wrapText = (text, maxW, fontSize) => {
      doc.setFontSize(fontSize);
      return doc.splitTextToSize(text, maxW);
    };

    // Parse the HTML into structured nodes and render each
    const tmp = document.createElement("div");
    tmp.innerHTML = formattedHtml;

    const stripHtml = (el) => el.innerText || el.textContent || "";

    const renderNode = (node) => {
      if (node.nodeType === 3) return; // bare text nodes at top level — skip
      const tag = node.tagName ? node.tagName.toLowerCase() : "";

      if (tag === "h1") {
        checkPage(14);
        y += 4;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(18);
        doc.setTextColor(...DARK);
        const lines = wrapText(stripHtml(node), contentW, 18);
        lines.forEach(line => { checkPage(8); doc.text(line, marginL, y); y += 8; });
        // accent underline
        doc.setDrawColor(...ACCENT);
        doc.setLineWidth(0.6);
        doc.line(marginL, y, marginL + contentW, y);
        y += 5;

      } else if (tag === "h2") {
        checkPage(12);
        y += 5;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        doc.setTextColor(...DARK);
        const lines = wrapText(stripHtml(node), contentW, 14);
        lines.forEach(line => { checkPage(7); doc.text(line, marginL, y); y += 7; });
        doc.setDrawColor(...LIGHT);
        doc.setLineWidth(0.3);
        doc.line(marginL, y, marginL + contentW, y);
        y += 4;

      } else if (tag === "h3") {
        checkPage(10);
        y += 3;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(12);
        doc.setTextColor(...ACCENT);
        const lines = wrapText(stripHtml(node), contentW, 12);
        lines.forEach(line => { checkPage(6); doc.text(line, marginL, y); y += 6; });
        y += 2;

      } else if (tag === "h4") {
        checkPage(8);
        y += 2;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(11);
        doc.setTextColor(...GREY);
        const lines = wrapText(stripHtml(node), contentW, 11);
        lines.forEach(line => { checkPage(6); doc.text(line, marginL, y); y += 6; });
        y += 1;

      } else if (tag === "p") {
        const text = stripHtml(node).trim();
        if (!text) { y += 2; return; }
        doc.setFont("helvetica", "normal");
        doc.setFontSize(10);
        doc.setTextColor(...DARK);
        const lines = wrapText(text, contentW, 10);
        lines.forEach(line => { checkPage(5.5); doc.text(line, marginL, y); y += 5.5; });
        y += 2;

      } else if (tag === "ul" || tag === "ol") {
        const items = node.querySelectorAll("li");
        items.forEach((li, idx) => {
          const text = stripHtml(li).trim();
          const bullet = tag === "ol" ? `${idx + 1}.` : "•";
          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);
          doc.setTextColor(...DARK);
          const lines = wrapText(text, contentW - 6, 10);
          lines.forEach((line, i) => {
            checkPage(5.5);
            if (i === 0) doc.text(bullet, marginL, y);
            doc.text(line, marginL + 6, y);
            y += 5.5;
          });
        });
        y += 2;

      } else if (tag === "blockquote") {
        const text = stripHtml(node).trim();
        doc.setFont("helvetica", "italic");
        doc.setFontSize(10);
        doc.setTextColor(...GREY);
        const lines = wrapText(text, contentW - 8, 10);
        const blockH = lines.length * 5.5 + 4;
        checkPage(blockH);
        doc.setFillColor(253, 241, 235);
        doc.rect(marginL, y - 3, contentW, blockH, "F");
        doc.setDrawColor(...ACCENT);
        doc.setLineWidth(0.8);
        doc.line(marginL, y - 3, marginL, y - 3 + blockH);
        lines.forEach(line => { doc.text(line, marginL + 5, y); y += 5.5; });
        y += 4;

      } else if (tag === "hr") {
        checkPage(6);
        y += 3;
        doc.setDrawColor(...LIGHT);
        doc.setLineWidth(0.3);
        doc.line(marginL, y, marginL + contentW, y);
        y += 4;

      } else if (tag === "table") {
        const headers = Array.from(node.querySelectorAll("th")).map(th => stripHtml(th).trim());
        const rows = Array.from(node.querySelectorAll("tr"))
          .filter(tr => tr.querySelectorAll("td").length > 0)
          .map(tr => Array.from(tr.querySelectorAll("td")).map(td => stripHtml(td).trim()));

        if (headers.length === 0 && rows.length === 0) return;
        const cols = Math.max(headers.length, rows[0]?.length || 1);
        const colW = contentW / cols;

        // Header row
        if (headers.length > 0) {
          checkPage(8);
          doc.setFillColor(240, 237, 232);
          doc.rect(marginL, y - 4, contentW, 7, "F");
          doc.setFont("helvetica", "bold");
          doc.setFontSize(9);
          doc.setTextColor(...DARK);
          headers.forEach((h, i) => doc.text(h.substring(0, 22), marginL + i * colW + 2, y));
          y += 4;
          doc.setDrawColor(...LIGHT);
          doc.setLineWidth(0.2);
          doc.line(marginL, y, marginL + contentW, y);
          y += 2;
        }
        // Data rows
        rows.forEach((row, ri) => {
          const rowH = 6;
          checkPage(rowH + 2);
          if (ri % 2 === 1) {
            doc.setFillColor(248, 246, 243);
            doc.rect(marginL, y - 4, contentW, rowH + 1, "F");
          }
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.setTextColor(...DARK);
          row.forEach((cell, i) => {
            const txt = doc.splitTextToSize(cell, colW - 3);
            doc.text(txt[0] || "", marginL + i * colW + 2, y);
          });
          y += rowH;
        });
        y += 4;

      } else {
        // div, section, or unknown — recurse into children
        Array.from(node.childNodes).forEach(renderNode);
      }
    };

    // Render all top-level children
    document.body.appendChild(tmp);
    Array.from(tmp.childNodes).forEach(renderNode);
    document.body.removeChild(tmp);

    // --- ADD BRANDING FOOTER TO ALL PAGES ---
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setDrawColor(218, 220, 224); // Google-style light border
      doc.setLineWidth(0.3);
      doc.line(marginL, pageH - 15, marginL + contentW, pageH - 15);

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      doc.setTextColor(26, 115, 232); // Google Blue
      doc.text("lliuq scribe.", marginL, pageH - 10);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(112, 117, 122); // Secondary Slate
      doc.text(`Generated via lliuq. Free for you!  |  Page ${i} of ${pageCount}`, marginL + contentW, pageH - 10, { align: "right" });
    }

    const filename = `smartscribe-${new Date().toISOString().slice(0, 10)}.pdf`;
    doc.save(filename);
    showToast("PDF exported");
  };

  const clearAll = () => {
    if (isRecording) return;
    setSegments([]);
    rawSegmentsRef.current = [];
    setIsFormatted(false);
    setFormattedHtml("");
    showToast("Cleared");
  };

  // ── Stats
  const allText = segments.map((s) => s.text).join(" ").trim();
  const wordCount = allText ? allText.split(/\s+/).filter(Boolean).length : 0;
  const charCount = allText.length;

  const fmtDisabled = !hasKey || !hasContent || !selectedFmt || isProcessing;

  return (
    <div style={{ background: "var(--bg)", minHeight: "100vh" }}>
      <style>{STYLES}</style>
      <div className="app-wrap">
        {/* Header */}
        <div className="app-header">
          <div>
            <div className="app-title">Lliuq <span style={{ fontWeight: 400, color: "var(--text3)" }}>·</span> <span style={{ color: "var(--accent)" }}>Scribe</span></div>
            <div className="app-sub">Live transcription & AI formatting</div>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            {/* Refined Ko-fi Link */}
            <a
              href="https://ko-fi.com/lliuqdeveloper"
              target="_blank"
              rel="noreferrer"
              className="status-pill"
              style={{
                textDecoration: 'none',
                background: '#f1f5f9',      /* Muted Slate Gray */
                color: '#475569',           /* Dark Slate Text */
                borderColor: '#e2e8f0',     /* Soft Border */
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = '#e2e8f0';
                e.currentTarget.style.color = '#0f172a';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = '#f1f5f9';
                e.currentTarget.style.color = '#475569';
              }}
            >
              <span style={{ fontSize: '14px' }}>☕</span>
              <span>Support the Project</span>
            </a>

            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {isRecording && lastSync && (
                <span style={{ fontSize: 11, color: "var(--text3)", fontFamily: "'JetBrains Mono', monospace" }}>
                  Last sync: {formatTime(lastSync)}
                </span>
              )}
              <span className={`status-pill ${status !== "idle" ? status : ""}`}>
                {statusLabel}
              </span>
            </span>
          </div>
        </div>

        <div className="card">
          {!hasKey ? (
            <div style={{ background: "rgba(200,80,40,0.05)", border: "1px solid rgba(200,80,40,0.15)", padding: "16px", borderRadius: 4, marginBottom: 12, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <p style={{ fontSize: 13, color: "#e06050", fontWeight: 500, margin: 0 }}>
                ⚠️ API Key Required: Please set your Groq key in the navigation bar to start.
              </p>
              <button className="btn btn-sm" onClick={() => window.location.href = "/"} style={{ padding: "0 12px", height: "26px", fontSize: "11px", whiteSpace: "nowrap" }}>Go Home</button>
            </div>
          ) : (
            <div className="field-label" style={{ color: "#2a7a3b" }}>✓ API Key Active</div>
          )}

          <div className="actions-row">
            <button
              className={`btn btn-record${isRecording ? " active" : ""}`}
              onClick={toggleRecord}
              disabled={!hasKey || isProcessing}
            >
              {isRecording ? (
                <>
                  <svg width="10" height="10" viewBox="0 0 10 10">
                    <rect x="2" y="2" width="6" height="6" rx="1" fill="currentColor" />
                  </svg>
                  Stop
                </>
              ) : (
                <>
                  <svg width="10" height="10" viewBox="0 0 10 10">
                    <circle cx="5" cy="5" r="5" fill="currentColor" />
                  </svg>
                  Record
                </>
              )}
            </button>

            {isRecording && (
              <div className="waveform">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="waveform-bar" style={{ animationDelay: `${i * 0.1}s` }} />
                ))}
              </div>
            )}

            <button
              className="btn"
              onClick={() => document.getElementById("file-input").click()}
              disabled={!hasKey || isProcessing || isRecording}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M2 10v3h12v-3M8 2v8M5 5l3-3 3 3" />
              </svg>
              Upload audio
            </button>
          </div>

          <input type="file" id="file-input" hidden accept="audio/*,video/*" onChange={handleUpload} />

          {progress.visible && (
            <div className="progress-wrap">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress.pct}%` }} />
              </div>
              <div className="progress-meta">
                <span>{progress.label}</span>
                <span>{progress.pct}%</span>
              </div>
            </div>
          )}

          <hr className="format-divider" />
          <div className="field-label">Auto-format style — select before formatting</div>

          <div className="format-chips">
            {FORMAT_TYPES.map((f) => (
              <div
                key={f.id}
                className={`fmt-chip${selectedFmt === f.id ? " selected" : ""}`}
                onClick={() => setSelectedFmt((prev) => (prev === f.id ? null : f.id))}
              >
                {f.icon}
                {f.label}
                <span className="fmt-chip-sub">{f.sub}</span>
              </div>
            ))}
          </div>

          <div className="model-selector-wrap">
            <div className="field-label" style={{ marginBottom: 6 }}>Formatting model — switch if quota is exhausted</div>
            <div className="model-chips">
              {[
                { id: "llama-3.3-70b-versatile", label: "Llama 3.3 70B", sub: "Best quality" },
                { id: "meta-llama/llama-4-scout-17b-16e-instruct", label: "Llama 4 Scout", sub: "Great for formating long text" },
              ].map((m) => (
                <div
                  key={m.id}
                  className={`model-chip${selectedModel === m.id ? " selected" : ""}`}
                  onClick={() => {
                    setSelectedModel(m.id);
                    localStorage.setItem("scribe_model", m.id);
                  }}
                >
                  {m.label}
                  <span className="model-chip-sub">{m.sub}</span>
                </div>
              ))}
            </div>
          </div>

          <button className="btn btn-format" onClick={formatTranscript} disabled={fmtDisabled}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M2 4h12M2 8h8M2 12h5" />
            </svg>
            {isProcessing && status === "processing" ? "Formatting..." : "Auto-format transcript"}
          </button>
        </div>

        {/* Transcript card */}
        <div className="card">
          <div className="transcript-header">
            <div className="transcript-meta">
              <span>{wordCount.toLocaleString()} words</span>
              <span>{charCount.toLocaleString()} chars</span>
              <span>{segments.length} seg{segments.length !== 1 ? "s" : ""}</span>
            </div>
            <div className="transcript-actions">
              <button className="btn-sm" onClick={copyText} disabled={!hasKey || !hasContent}>
                Copy
              </button>
              <button className="btn-sm" onClick={downloadTxt} disabled={!hasKey || !hasContent}>
                Export .txt
              </button>

              {isFormatted && (
                <button className="btn-sm" onClick={() => {
                  setSegments(rawSegmentsRef.current);
                  setIsFormatted(false);
                  setFormattedHtml("");
                  setIsEditing(false);
                  showToast("Returned to raw transcript");
                }}>
                  ← Raw
                </button>
              )}
              {isFormatted && (
                <button
                  className="btn-sm"
                  onClick={async () => {
                    if (isEditing) {
                      // re-render markdown after editing
                      if (!window.marked) {
                        await new Promise((resolve, reject) => {
                          const s = document.createElement("script");
                          s.src = "https://cdn.jsdelivr.net/npm/marked/marked.min.js";
                          s.onload = resolve; s.onerror = reject;
                          document.head.appendChild(s);
                        });
                      }
                      window.marked.setOptions({ breaks: true });
                      const html = window.marked.parse(segments[0]?.text || "");
                      setFormattedHtml(html);
                      showToast("Preview updated");
                    }
                    setIsEditing(e => !e);
                  }}
                  style={isEditing ? { background: "var(--info-bg)", color: "var(--info)", borderColor: "var(--info-border)" } : {}}
                >
                  {isEditing ? "✓ Preview" : "✏ Edit"}
                </button>
              )}
              <button
                className={`btn-sm${isFormatted ? " btn-sm-pdf" : ""}`}
                onClick={exportPdf}
                disabled={!isFormatted}
                title={isFormatted ? "Export formatted document as PDF" : "Format the transcript first"}
              >
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 2h7l3 3v9H3V2z" /><path d="M10 2v3h3" /><path d="M6 9h4M6 12h2" />
                </svg>
                Export PDF
              </button>
              <button className="btn-sm" onClick={clearAll} disabled={!hasContent || isRecording}>
                Clear
              </button>
            </div>
          </div>

          <div className={`transcript-box${isFormatted && !isEditing ? " transcript-box--formatted" : ""}`} ref={boxRef}>
            {segments.length === 0 ? (
              <span className="transcript-placeholder">
                {hasKey
                  ? "Start recording or upload an audio file to begin transcribing..."
                  : "Enter your Groq API key above to unlock all features."}
              </span>
            ) : isFormatted && isEditing ? (
              <textarea
                style={{ width: "100%", minHeight: 400, background: "transparent", border: "none", outline: "none", fontFamily: "'Inter', sans-serif", fontSize: 14, lineHeight: 1.8, color: "var(--text)", resize: "vertical", fontStyle: "italic" }}
                value={segments[0]?.text || ""}
                onChange={e => setSegments([{ time: segments[0]?.time || formatTime(), text: e.target.value }])}
              />
            ) : isFormatted ? (
              <div
                className="formatted-content"
                dangerouslySetInnerHTML={{ __html: formattedHtml }}
              />
            ) : (
              segments.map((seg, i) => (
                <div key={i}>
                  {isRecording && i === segments.length - 1 && (
                    <span className="rec-dot" />
                  )}
                  <div className="segment">
                    <span className="segment-time">{seg.time}</span>
                    {seg.text}
                  </div>
                  {i < segments.length - 1 && <hr className="seg-divider" />}
                </div>
              ))
            )}
          </div>
        </div>
      </div>


      <div className={toast.show ? "toast show" : "toast"}>{toast.msg}</div>
    </div>
  );
}